<template>
	<div class="card-detailed">
		<div class="transaction">
			<div ref="headerEl" class="header">
				<div class="header-top">
					<div class="price-block">
						<span class="price-label">{{ t("transaction.card.fields.salePrice") }}</span>
						<h1 class="price">
							<span class="currency">$</span>
							<span class="amount">{{ formatQuantity(roundTo(transaction.deal?.salePrice, 1)) }}</span>
						</h1>
					</div>
					<div class="header-actions">
						<button
							v-if="!compact"
							type="button"
							class="icon-button collapse-btn"
							:title="t('callToActions.collapsePanel')"
							@click="emit('collapse')"
						>
							<AppIcon :name="IconName.PanelRightCollapse" :size="18" :stroke-width="1.2" />
						</button>
						<button
							type="button"
							class="icon-button collapse-btn"
							:title="t('callToActions.close')"
							@click="emit('close')"
						>
							<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
						</button>
					</div>
				</div>

				<h3 class="address" :class="{ unavailable: !addressLines.street }">
					{{ addressLines.street || $t("unknownAddress") }}
				</h3>
				<div class="borough">
					{{ addressLines.cityLine }}
				</div>

				<div class="badge-actions-row">
					<div class="badge-row">
						<span class="type-badge">
							{{ isAssemblyTransaction ? t("transaction.card.assemblyBadge") : t("transaction.card.badge") }}
						</span>
						<span v-if="selectedStatus" class="status-badge">{{ selectedStatus }}</span>
					</div>

					<div class="price-row-actions">
						<Button
							v-if="isWriter && (!canEditAssembly || isEditMode)"
							class="toggle-edit-btn"
							size="s"
							variant="primary"
							@click.stop="toggleEditMode"
						>
							{{ isEditMode ? t("transaction.card.exitEditMode") : t("transaction.card.enterEditMode") }}
						</Button>
						<Menu v-else-if="isWriter" @select="onEditMenuSelect">
							<template #trigger>
								<Button class="toggle-edit-btn" size="s" variant="primary">
									{{ t("transaction.card.enterEditMode") }}
									<AppIcon :name="IconName.ChevronDown" class="menu-trigger-chevron" :size="10" :stroke-width="2" />
								</Button>
							</template>
							<MenuItem value="info">{{ t("transaction.card.editMenuInformation") }}</MenuItem>
							<MenuItem value="composition">{{ t("transaction.card.editComponentTransactions") }}</MenuItem>
						</Menu>
						<TransactionSelectionButton
							:transaction="transaction"
							:can-select="isWriter"
							@export-selection-changed="emit('list-add')"
						/>
					</div>
				</div>
			</div>
			<div class="separator"></div>

			<div ref="itemsEl" class="items">
				<div v-if="isEditMode || codificationDate" class="transaction-status">
					<div v-if="isEditMode" class="status">
						<h2 class="label">{{ t("transaction.card.selectStatus") }}</h2>
						<div class="control">
							<ArkListboxDropdown
								:model-value="selectedStatus"
								:options="statusOptions"
								:multiple="false"
								:placeholder="t('transaction.card.noSelection')"
								class="status-dropdown"
								@update:model-value="onStatusChange"
							/>
						</div>
					</div>
					<EditableField
						:readonly="!isEditMode"
						v-if="codificationDate"
						type="date"
						:title="t('transaction.card.codificationDate')"
						name="metadataDoneAt"
						:value="formatTransactionDate(codificationDate)"
						disabled
					/>
				</div>

				<AccordionRoot :multiple="true" :default-value="['info']" persistence-key="transaction-card-accordion">
					<AccordionItem value="info" :title="t('transaction.card.accordion.info')">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.info.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.info = !expandAllSections.info"
							>
								{{
									expandAllSections.info
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.inscriptionNumber')"
								name="inscriptionNumber"
								:tooltip="t('fieldTooltip.inscriptionNumber')"
								:value="formatInscriptionNumber(transaction.inscriptionNumber)"
								:copy-title="t('callToActions.copyInscriptionNumber')"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										formatInscriptionNumber(component.inscriptionNumber)
									)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="url"
								:title="t('transaction.card.fields.deedOfSale')"
								name="deedOfSaleUrl"
								:tooltip="t('fieldTooltip.deedOfSale')"
								:value="transaction.deedOfSaleUrl"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(
										transaction.components,
										'inscriptionNumber',
										component => getUrlTrailingSegments(component.deedOfSaleUrl, 2) || 'N/A',
										component => component.deedOfSaleUrl
									)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="date"
								:title="t('transaction.card.fields.deedOfSaleDate')"
								name="deedOfSaleDate"
								:tooltip="t('fieldTooltip.deedOfSaleDate')"
								:value="formatTransactionDate(transaction.deedOfSaleDate)"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										formatTransactionDate(component.deedOfSaleDate)
									)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="date"
								:title="t('transaction.card.fields.psaDate')"
								name="psaDate"
								:tooltip="t('fieldTooltip.psaDate')"
								:value="formatTransactionDate(transaction.psaDate)"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										formatTransactionDate(component.psaDate)
									)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="isWriter && canReadConfidential"
								info
								type="text"
								:title="t('transaction.card.fields.cmsdCategory')"
								name="cmsdCategory"
								:tooltip="t('fieldTooltip.cmsdCategory')"
								:value="transaction.category"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'category')"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<div v-if="!isEditMode" class="parcels-wrapper">
								<button
									v-if="dedupedParcelNumbers.length > 1"
									type="button"
									class="parcels-toggle"
									@click="isParcelsExpanded = !isParcelsExpanded"
								>
									{{ isParcelsExpanded ? t("parcels.hide") : t("parcels.showAll") }}
								</button>
								<div class="detail parcels-detail">
									<span class="label">{{ t("transaction.card.fields.parcels") }}</span>
									<span v-if="dedupedParcelNumbers.length > 0" class="value parcels">
										<span class="primary-parcel">{{ dedupedParcelNumbers[0] }}</span>
										<span v-if="!isParcelsExpanded && dedupedParcelNumbers.length > 1" class="parcels-count">
											+{{ dedupedParcelNumbers.length - 1 }}
										</span>
										<span v-if="isParcelsExpanded && dedupedParcelNumbers.length > 1" class="parcels-rest"
											>, {{ dedupedParcelNumbers.slice(1).join(", ") }}</span
										>
									</span>
									<span v-else class="value empty">—</span>
									<CopyButton
										v-if="dedupedParcelNumbers.length > 0"
										class="copy"
										:text="dedupedParcelNumbers.join(', ')"
										:title="t('callToActions.copyLotNumbers')"
										size="md"
									/>
								</div>
							</div>
							<EditableField
								v-else
								type="textarea"
								:title="t('transaction.card.fields.parcels')"
								name="parcelIds"
								:tooltip="t('fieldTooltip.parcelIds')"
								:value="spaceOutNumbers(...(transaction.location?.parcelNumbers ?? [])).join(', ')"
								:copy-title="t('callToActions.copyLotNumbers')"
								@input="
									field => {
										field.value = normalizeParcelIds(field.value)
										onFieldInput(field)
									}
								"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										spaceOutNumbers(...(component.location?.parcelNumbers ?? [])).join(', ')
									)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.totalLandArea')"
								:symbol="areaUnitSymbol"
								:name="TransactionAreaField.TotalLandArea"
								:tooltip="t('fieldTooltip.superficy')"
								:value="transactionAreaValue(transaction, TransactionAreaField.TotalLandArea, areaUnit)"
								:copy-title="t('callToActions.copyTotalArea')"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionAreaValue(component, TransactionAreaField.TotalLandArea, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.buildingArea')"
								:symbol="areaUnitSymbol"
								:name="TransactionAreaField.BuildingArea"
								:tooltip="t('fieldTooltip.buildingAreaSqft')"
								:value="transactionAreaValue(transaction, TransactionAreaField.BuildingArea, areaUnit)"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionAreaValue(component, TransactionAreaField.BuildingArea, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.01"
								:title="t('transaction.card.fields.salePrice')"
								symbol="$"
								name="salePrice"
								:tooltip="t('fieldTooltip.salePrice')"
								:value="transaction.salePrice"
								:copy-title="t('callToActions.copySalePrice')"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.salePrice)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="isWriter"
								type="number"
								:rounding="0.01"
								:title="t('transaction.card.fields.ownershipPercentage')"
								symbol="%"
								name="ownershipPercentage"
								:tooltip="t('fieldTooltip.ownershipPercentage')"
								:value="transaction.ownershipPercentage"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.ownershipPercentage)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="url"
								:title="t('transaction.card.fields.assessmentRole')"
								name="cityInformationSheetUrl"
								:tooltip="t('fieldTooltip.cityInformationSheet')"
								:value="transaction.cityInformationSheetUrl"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(
										transaction.components,
										'inscriptionNumber',
										component => getUrlTrailingSegments(component.cityInformationSheetUrl, 2) || 'N/A',
										component => component.cityInformationSheetUrl
									)
								"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="textarea"
								:title="t('transaction.card.fields.comments')"
								name="comments"
								:tooltip="t('fieldTooltip.comments')"
								:value="transaction.comments"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'comments')"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="textarea"
								:title="t('transaction.card.fields.projectComments')"
								name="projectComments"
								:tooltip="t('fieldTooltip.projectComments')"
								:value="transaction.projectComments"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'projectComments')"
								:expand-all-values="expandAllSections.info"
								:collapsible-registry="collapsibleFieldIds.info"
							/>
							<div class="drive-upload-section">
								<div class="drive-upload-field">
									<span class="drive-label">{{ t("transaction.card.fields.driveFolder") }}</span>
									<span v-if="transaction.driveFolderId" class="drive-link-group">
										<span v-if="transaction.driveFileCount != null" class="drive-file-count">
											{{
												t(
													"transaction.card.fields.driveFileCount",
													{ count: transaction.driveFileCount },
													transaction.driveFileCount
												)
											}}
										</span>
										<a
											:href="`https://drive.google.com/drive/folders/${transaction.driveFolderId}`"
											target="_blank"
											rel="noopener noreferrer"
											class="link-pill"
										>
											{{ t("transaction.card.openDocument") }}
											<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
										</a>
									</span>
									<span v-else-if="!isEditMode" class="drive-empty">—</span>
								</div>
								<FileUpload
									v-if="canUploadDocument && isEditMode"
									:on-upload="handleFileUpload"
									:idle-label="t('transaction.card.fields.uploadDocument')"
									:success-label="t('transaction.card.fields.uploadSuccess')"
								/>
							</div>
						</div>
					</AccordionItem>

					<AccordionItem value="classification" :title="t('transaction.card.accordion.classification')">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.classification.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.classification = !expandAllSections.classification"
							>
								{{
									expandAllSections.classification
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.assetType')"
								name="assetType"
								:tooltip="t('fieldTooltip.assetType')"
								:options="assetTypeOptions"
								:value="transaction.assetType || []"
								:multiple="true"
								@input="onClassificationUpdate"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'assetType')"
								:expand-all-values="expandAllSections.classification"
								:collapsible-registry="collapsibleFieldIds.classification"
							/>

							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.landCategory')"
								name="landCategoryNew"
								:tooltip="t('fieldTooltip.landCategoryNew')"
								:options="landCategoryOptions"
								:value="transaction.landCategoryNew || []"
								:multiple="true"
								@input="onClassificationUpdate"
								:disabled="!isEditMode || !hasLandAssetType"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'landCategoryNew')"
								:expand-all-values="expandAllSections.classification"
								:collapsible-registry="collapsibleFieldIds.classification"
							/>

							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.landSubcategory')"
								name="landSubcategory"
								:tooltip="t('fieldTooltip.landSubcategory')"
								:options="landSubcategoryOptions"
								:value="transaction.landSubcategory || []"
								:multiple="true"
								@input="onClassificationUpdate"
								:disabled="!isEditMode || !hasRelevantLandCategory"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'landSubcategory')"
								:expand-all-values="expandAllSections.classification"
								:collapsible-registry="collapsibleFieldIds.classification"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="isWriter"
								type="checkbox"
								:title="t('transaction.card.fields.isGrossLand')"
								name="isGrossLand"
								:tooltip="t('fieldTooltip.isGrossLand')"
								:value="transaction.isGrossLand"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'isGrossLand')"
								:expand-all-values="expandAllSections.classification"
								:collapsible-registry="collapsibleFieldIds.classification"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.currentState')"
								name="currentState"
								:tooltip="t('fieldTooltip.currentState')"
								:options="TransactionCurrentState"
								:value="transaction.currentState"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'currentState')"
								:expand-all-values="expandAllSections.classification"
								:collapsible-registry="collapsibleFieldIds.classification"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.constructionYear')"
								name="constructionYear"
								:tooltip="t('fieldTooltip.constructionYear')"
								:value="formatConstructionYears(transaction)"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', formatConstructionYears)"
								:expand-all-values="expandAllSections.classification"
								:collapsible-registry="collapsibleFieldIds.classification"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.propertyUse')"
								name="propertyUse"
								:tooltip="t('fieldTooltip.propertyUse')"
								:value="formatPropertyUses(transaction)"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', formatPropertyUses)"
								:expand-all-values="expandAllSections.classification"
								:collapsible-registry="collapsibleFieldIds.classification"
							/>
						</div>
					</AccordionItem>

					<AccordionItem value="pricing" :title="t('transaction.card.accordion.pricing')">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.pricing.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.pricing = !expandAllSections.pricing"
							>
								{{
									expandAllSections.pricing
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.01"
								:title="t('transaction.card.fields.priceLand')"
								:symbol="pricePerAreaUnitSymbol"
								:name="TransactionPricePerAreaField.Land"
								:tooltip="t('fieldTooltip.pricePerLandSqft')"
								:value="transactionPricePerAreaValue(transaction, TransactionPricePerAreaField.Land, areaUnit)"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionPricePerAreaValue(component, TransactionPricePerAreaField.Land, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.pricing"
								:collapsible-registry="collapsibleFieldIds.pricing"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="number"
								:rounding="0.01"
								:title="t('transaction.card.fields.priceDevelopable')"
								:symbol="pricePerAreaUnitSymbol"
								:name="TransactionPricePerAreaField.Developable"
								:value="transactionPricePerAreaValue(transaction, TransactionPricePerAreaField.Developable, areaUnit)"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionPricePerAreaValue(component, TransactionPricePerAreaField.Developable, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.pricing"
								:collapsible-registry="collapsibleFieldIds.pricing"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.01"
								:title="t('transaction.card.fields.priceBuildable')"
								:symbol="pricePerAreaUnitSymbol"
								:name="TransactionPricePerAreaField.Buildable"
								:tooltip="t('fieldTooltip.pricePerBuildableSqft')"
								:value="transactionPricePerAreaValue(transaction, TransactionPricePerAreaField.Buildable, areaUnit)"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionPricePerAreaValue(component, TransactionPricePerAreaField.Buildable, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.pricing"
								:collapsible-registry="collapsibleFieldIds.pricing"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.01"
								:title="t('transaction.card.fields.priceBuilding')"
								:symbol="pricePerAreaUnitSymbol"
								:name="TransactionPricePerAreaField.Building"
								:tooltip="t('transaction.buildingMetrics.tooltip')"
								:value="transactionPricePerAreaValue(transaction, TransactionPricePerAreaField.Building, areaUnit)"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionPricePerAreaValue(component, TransactionPricePerAreaField.Building, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.pricing"
								:collapsible-registry="collapsibleFieldIds.pricing"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="100"
								:title="t('transaction.card.fields.priceUnit')"
								symbol="$"
								name="pricePerUnit"
								:tooltip="t('fieldTooltip.pricePerUnit')"
								:value="transaction.pricePerUnit"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.pricePerUnit)
								"
								:expand-all-values="expandAllSections.pricing"
								:collapsible-registry="collapsibleFieldIds.pricing"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.01"
								:title="t('transactions-table.columns.gross-annual-rent')"
								name="grossAnnualRent"
								:value="transaction.grossAnnualRent"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'grossAnnualRent')"
								:expand-all-values="expandAllSections.pricing"
								:collapsible-registry="collapsibleFieldIds.pricing"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.01"
								:title="t('transactions-table.columns.gross-monthly-rent-per-unit')"
								name="grossMonthlyRentPerUnit"
								:value="transaction.grossMonthlyRentPerUnit"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'grossMonthlyRentPerUnit')"
								:expand-all-values="expandAllSections.pricing"
								:collapsible-registry="collapsibleFieldIds.pricing"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.01"
								:title="t('transactions-table.columns.gross-rent-multiplier')"
								name="grossRentMultiplier"
								:value="transaction.grossRentMultiplier"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'grossRentMultiplier')"
								:expand-all-values="expandAllSections.pricing"
								:collapsible-registry="collapsibleFieldIds.pricing"
							/>
						</div>
					</AccordionItem>

					<AccordionItem value="location" :title="t('transaction.card.accordion.location')">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.location.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.location = !expandAllSections.location"
							>
								{{
									expandAllSections.location
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.address')"
								name="address"
								:tooltip="t('fieldTooltip.address')"
								:value="transaction.location?.address"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.location?.address)
								"
								:expand-all-values="expandAllSections.location"
								:collapsible-registry="collapsibleFieldIds.location"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.municipality')"
								name="municipalityName"
								:tooltip="t('fieldTooltip.municipalityName')"
								:options="municipalities"
								:value="transaction.location?.municipality"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(
										transaction.components,
										'inscriptionNumber',
										component => component.location?.municipality
									)
								"
								:expand-all-values="expandAllSections.location"
								:collapsible-registry="collapsibleFieldIds.location"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.postalCode')"
								name="postalCode"
								:tooltip="t('fieldTooltip.postalCode')"
								:value="spaceOutNumbers(transaction.postalCode).join(', ')"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										spaceOutNumbers(component.postalCode).join(', ')
									)
								"
								:expand-all-values="expandAllSections.location"
								:collapsible-registry="collapsibleFieldIds.location"
							/>
						</div>
					</AccordionItem>

					<AccordionItem value="parties" :title="t('transaction.card.accordion.parties')">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.parties.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.parties = !expandAllSections.parties"
							>
								{{
									expandAllSections.parties
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.saleContext')"
								name="saleContext"
								:tooltip="t('fieldTooltip.saleContext')"
								:options="TransactionSaleContext"
								:value="transaction.saleContext"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'saleContext')"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>

							<h2>{{ t("transaction.card.sections.buyer") }}</h2>

							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.buyerStatus')"
								:tooltip="t('fieldTooltip.buyerStatus')"
								name="metadataBuyerStatus"
								:options="buyerStatusOptions"
								:value="transaction.metadata?.buyerStatus"
								@input="onFieldInput"
								:disabled="!isEditMode"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="buyerCodificationDate"
								type="date"
								:title="t('transaction.card.buyerCodificationDate')"
								name="metadataBuyerDoneAt"
								:value="formatTransactionDate(buyerCodificationDate)"
								disabled
							/>

							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.name')"
								name="buyer"
								:tooltip="t('fieldTooltip.buyer')"
								:value="transaction.buyer?.name"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.buyer?.name)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.type')"
								name="buyerType"
								:tooltip="t('fieldTooltip.buyerType')"
								:options="TransactionPartyType"
								:value="transaction.buyer?.type"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.buyer?.type)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.neq')"
								name="buyerNeq"
								:tooltip="t('fieldTooltip.buyerNeq')"
								:value="transaction.buyer?.neq"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.buyer?.neq)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.activity')"
								name="buyerActivity"
								:tooltip="t('fieldTooltip.buyerActivity')"
								:value="transaction.buyer?.activity"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.buyer?.activity)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>

							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.representativeName')"
								name="buyerRepresentativeFullName"
								:tooltip="t('fieldTooltip.buyerRepresentativeFullName')"
								:value="[transaction.buyerRepresentativeFirstName, transaction.buyerRepresentativeLastName].join(' ')"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										[component.buyerRepresentativeFirstName, component.buyerRepresentativeLastName]
											.filter(Boolean)
											.join(' ')
									)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>

							<h2>{{ t("transaction.card.sections.buyerContacts") }}</h2>

							<Button v-if="isEditMode" variant="external" size="m" @click="contactModalIsOpen = true">
								{{ t("transaction.card.addBuyerContacts") }}
							</Button>

							<div class="contacts">
								<div
									v-for="contact in sortContacts(transaction.contacts)"
									:key="contact?.id || undefined"
									class="contact"
									:title="contact.address || $t('unknownAddress')"
								>
									<div class="info">
										<div class="header">
											<span class="name">
												{{ contact.firstName }} {{ contact.lastName }}
												<span v-if="contact.primaryCompany?.name" class="company">
													- {{ contact.primaryCompany.name }}
												</span>
											</span>
											<button
												v-if="isEditMode"
												type="button"
												class="remove"
												@click="() => handleRemoveContact(transaction, contact)"
											>
												<AppIcon :name="IconName.CloseSmall" :size="12" :stroke-width="1.3" />
											</button>
										</div>
										<span class="email">{{ contact.email }}</span>
										<Button
											v-if="isEditMode"
											variant="ghost"
											size="xs"
											class="change-company-link"
											@click.stop.prevent="
												companyModal && ((companyModal.targetContact = contact), (companyModalIsOpen = true))
											"
										>
											{{
												contact.primaryCompany?.name
													? t("transaction.card.changeCompany")
													: t("transaction.card.addCompany")
											}}
										</Button>
									</div>
								</div>
							</div>

							<h2>{{ t("transaction.card.sections.seller") }}</h2>

							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.name')"
								name="seller"
								:tooltip="t('fieldTooltip.seller')"
								:value="transaction.seller?.name"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.seller?.name)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.type')"
								name="sellerType"
								:tooltip="t('fieldTooltip.sellerType')"
								:options="TransactionPartyType"
								:value="transaction.seller?.type"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.seller?.type)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.neq')"
								name="sellerNeq"
								:tooltip="t('fieldTooltip.sellerNeq')"
								:value="transaction.seller?.neq"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.seller?.neq)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.activity')"
								name="sellerActivity"
								:tooltip="t('fieldTooltip.sellerActivity')"
								:value="transaction.seller?.activity"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.seller?.activity)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>

							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.representativeName')"
								name="sellerRepresentativeFullName"
								:tooltip="t('fieldTooltip.sellerRepresentativeFullName')"
								:value="[transaction.sellerRepresentativeFirstName, transaction.sellerRepresentativeLastName].join(' ')"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										[component.sellerRepresentativeFirstName, component.sellerRepresentativeLastName]
											.filter(Boolean)
											.join(' ')
									)
								"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>

							<h2 v-if="canReadConfidential">{{ t("transaction.card.sections.broker") }}</h2>

							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="text"
								:title="t('transaction.card.fields.broker')"
								name="broker"
								:tooltip="t('fieldTooltip.broker')"
								:value="transaction.broker"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'broker')"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="text"
								:title="t('transaction.card.fields.agency')"
								name="agency"
								:tooltip="t('fieldTooltip.agency')"
								:value="transaction.agency"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'agency')"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="text"
								:title="t('transaction.card.fields.marketplace')"
								name="marketplace"
								:tooltip="t('fieldTooltip.marketplace')"
								:value="transaction.marketplace"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'marketplace')"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="text"
								:title="t('transaction.card.fields.marketplaceId')"
								name="marketplaceId"
								:tooltip="t('fieldTooltip.marketplaceId')"
								:value="transaction.marketplaceId"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'marketplaceId')"
								:expand-all-values="expandAllSections.parties"
								:collapsible-registry="collapsibleFieldIds.parties"
							/>
						</div>
					</AccordionItem>

					<AccordionItem value="lender" :title="t('transaction.card.accordion.lender')" v-if="canReadConfidential">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.lender.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.lender = !expandAllSections.lender"
							>
								{{
									expandAllSections.lender
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="checkbox"
								:title="t('transaction.card.fields.mortgage')"
								name="mortgage"
								:tooltip="t('fieldTooltip.mortgage')"
								:value="transaction.mortgage"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'mortgage')"
								:expand-all-values="expandAllSections.lender"
								:collapsible-registry="collapsibleFieldIds.lender"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="text"
								:title="t('transaction.card.fields.lenderName')"
								name="lender"
								:tooltip="t('fieldTooltip.lender')"
								:value="transaction.lender"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'lender')"
								:expand-all-values="expandAllSections.lender"
								:collapsible-registry="collapsibleFieldIds.lender"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="number"
								:rounding="0.01"
								:title="t('transaction.card.fields.lenderAmount')"
								symbol="$"
								name="lenderAmount"
								:tooltip="t('fieldTooltip.lenderAmount')"
								:value="transaction.lenderAmount"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.lenderAmount)
								"
								:expand-all-values="expandAllSections.lender"
								:collapsible-registry="collapsibleFieldIds.lender"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="checkbox"
								:title="t('transaction.card.fields.salesBalance')"
								name="salesBalance"
								:tooltip="t('fieldTooltip.salesBalance')"
								:value="transaction.salesBalance"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'salesBalance')"
								:expand-all-values="expandAllSections.lender"
								:collapsible-registry="collapsibleFieldIds.lender"
							/>
						</div>
					</AccordionItem>

					<AccordionItem value="zoning" :title="t('transaction.card.accordion.zoning')">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.zoning.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.zoning = !expandAllSections.zoning"
							>
								{{
									expandAllSections.zoning
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								type="url"
								:title="t('transaction.card.fields.planImage')"
								name="zoningImageUrl"
								:tooltip="t('fieldTooltip.zoningImage')"
								:value="transaction.zoningImageUrl"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(
										transaction.components,
										'inscriptionNumber',
										component => getUrlTrailingSegments(component.zoningImageUrl, 2) || 'N/A',
										component => component.zoningImageUrl
									)
								"
								:expand-all-values="expandAllSections.zoning"
								:collapsible-registry="collapsibleFieldIds.zoning"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="url"
								download
								:title="t('transaction.card.fields.gridFile')"
								name="zoningReportUrl"
								:tooltip="t('fieldTooltip.zoningReport')"
								:value="transaction.zoningReportUrl"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(
										transaction.components,
										'inscriptionNumber',
										component => getUrlTrailingSegments(component.zoningReportUrl, 2) || 'N/A',
										component => component.zoningReportUrl
									)
								"
								:expand-all-values="expandAllSections.zoning"
								:collapsible-registry="collapsibleFieldIds.zoning"
							/>
						</div>
					</AccordionItem>

					<AccordionItem value="development" :title="t('transaction.card.accordion.development')">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.development.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.development = !expandAllSections.development"
							>
								{{
									expandAllSections.development
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.projectStatus')"
								name="codificationType"
								:tooltip="t('fieldTooltip.codificationType')"
								:options="TransactionCodificationType"
								:value="transaction.codificationType"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'codificationType')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>

							<h2>{{ t("transaction.card.sections.project") }}</h2>

							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.currentApproval')"
								name="projectApproval"
								:tooltip="t('fieldTooltip.projectApproval')"
								:options="TransactionProjectApproval"
								:value="transaction.projectApproval"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'projectApproval')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.approvalAtDeedOfSale')"
								name="projectApprovalAtDeedOfSale"
								:tooltip="t('fieldTooltip.projectApprovalAtDeedOfSale')"
								:options="TransactionProjectApprovalAtDeedOfSale"
								:value="transaction.projectApprovalAtDeedOfSale"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', 'projectApprovalAtDeedOfSale')
								"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="url"
								:title="t('transaction.card.fields.link')"
								name="projectLink"
								:tooltip="t('fieldTooltip.projectLink')"
								:value="transaction.projectLink"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'projectLink')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="url"
								:title="t('transaction.card.fields.document')"
								name="projectDocument"
								:tooltip="t('fieldTooltip.projectDocument')"
								:value="transaction.projectDocument"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'projectDocument')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.stories')"
								name="stories"
								:tooltip="t('fieldTooltip.stories')"
								:value="transaction.stories"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'stories')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.storiesStatus')"
								name="metadataStoriesStatus"
								:tooltip="t('fieldTooltip.storiesStatus')"
								:options="fieldStatusOptions"
								:value="transaction.metadata?.storiesStatus"
								@input="onFieldInput"
								:disabled="!isEditMode"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.1"
								:title="t('transaction.card.fields.siteCoverageRatio')"
								symbol="%"
								name="siteCoverageRatio"
								:tooltip="t('fieldTooltip.siteCoverageRatio')"
								:value="transaction.siteCoverageRatio"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.siteCoverageRatio)
								"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.000001"
								:title="t('transaction.card.fields.density')"
								name="density"
								:tooltip="t('fieldTooltip.density')"
								:value="transaction.density"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.density)
								"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.buildableArea')"
								:symbol="areaUnitSymbol"
								:name="TransactionAreaField.BuildableArea"
								:tooltip="t('fieldTooltip.buildableAreaSqft')"
								:value="transactionAreaValue(transaction, TransactionAreaField.BuildableArea, areaUnit)"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionAreaValue(component, TransactionAreaField.BuildableArea, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.buildableAreaStatus')"
								name="metadataBuildableAreaStatus"
								:tooltip="t('fieldTooltip.buildableAreaStatus')"
								:options="fieldStatusOptions"
								:value="transaction.metadata?.buildableAreaStatus"
								@input="onFieldInput"
								:disabled="!isEditMode"
							/>

							<h2>{{ t("transaction.card.sections.parking") }}</h2>

							<TransactionParkingField
								:parking-types="transaction.parkingTypes"
								:has-indoor-parking="transaction.hasIndoorParking"
								:has-outdoor-parking="transaction.hasOutdoorParking"
								:readonly="!isEditMode"
								:disabled="!isEditMode"
								@input="onFieldInput"
							/>

							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.parkingOutdoor')"
								name="parkingOutdoorCount"
								:tooltip="t('fieldTooltip.parkingOutdoorCount')"
								:value="transaction.parkingOutdoorCount"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'parkingOutdoorCount')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.parkingIndoor')"
								name="parkingIndoorCount"
								:tooltip="t('fieldTooltip.parkingIndoorCount')"
								:value="transaction.parkingIndoorCount"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'parkingIndoorCount')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.parkingTotal')"
								name="parkingTotal"
								:tooltip="t('fieldTooltip.parkingTotal')"
								:value="transaction.parkingTotal"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'parkingTotal')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.1"
								:title="t('transaction.card.fields.parkingRatio')"
								name="parkingRatio"
								:tooltip="t('fieldTooltip.parkingRatio')"
								:value="transaction.parkingRatio"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.parkingRatio)
								"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>

							<h2>{{ t("transaction.card.sections.units") }}</h2>

							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.unitsTotal')"
								name="numberOfUnits"
								:tooltip="t('fieldTooltip.numberOfUnits')"
								:value="transaction.numberOfUnits"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'numberOfUnits')"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.unitsTotalStatus')"
								name="metadataNumberOfUnitsStatus"
								:tooltip="t('fieldTooltip.numberOfUnitsStatus')"
								:options="fieldStatusOptions"
								:value="transaction.metadata?.numberOfUnitsStatus"
								@input="onFieldInput"
								:disabled="!isEditMode"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.unitsArea')"
								:symbol="areaUnitSymbol"
								:name="TransactionAreaField.UnitArea"
								:tooltip="t('fieldTooltip.unitAreaSqft')"
								:value="transactionAreaValue(transaction, TransactionAreaField.UnitArea, areaUnit)"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionAreaValue(component, TransactionAreaField.UnitArea, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.development"
								:collapsible-registry="collapsibleFieldIds.development"
							/>
						</div>
					</AccordionItem>

					<AccordionItem
						v-if="canReadConfidential"
						value="dataProvider"
						:title="t('transaction.card.accordion.dataProvider')"
					>
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.dataProvider.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.dataProvider = !expandAllSections.dataProvider"
							>
								{{
									expandAllSections.dataProvider
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								v-if="isWriter && canReadConfidential"
								type="textarea"
								:title="t('transaction.card.fields.constructabilityComments')"
								name="constructabilityComments"
								:tooltip="t('fieldTooltip.constructabilityComments')"
								:value="transaction.constructabilityComments"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'constructabilityComments')"
								:expand-all-values="expandAllSections.dataProvider"
								:collapsible-registry="collapsibleFieldIds.dataProvider"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="isWriter && canReadConfidential"
								type="textarea"
								:title="t('transaction.card.fields.classificationComments')"
								name="classificationComments"
								:tooltip="t('fieldTooltip.classificationComments')"
								:value="transaction.classificationComments"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'classificationComments')"
								:expand-all-values="expandAllSections.dataProvider"
								:collapsible-registry="collapsibleFieldIds.dataProvider"
							/>
							<!--
							Subcategory is disabled for the transition to Land Category.
							TODO remove once Land Category is in prod and stable
							-->
							<EditableField
								:readonly="!isEditMode"
								v-if="isWriter && canReadConfidential"
								type="text"
								:title="t('transaction.card.fields.subcategory')"
								name="subcategory"
								:tooltip="t('fieldTooltip.subcategory')"
								:options="Object.values(TransactionSubcategory)"
								:value="transaction.subcategory"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'subcategory')"
								:expand-all-values="expandAllSections.dataProvider"
								:collapsible-registry="collapsibleFieldIds.dataProvider"
							/>
							<EditableField
								:readonly="!isEditMode"
								v-if="canReadConfidential"
								type="url"
								:title="t('transaction.card.fields.cmsd3dModel')"
								name="volumetricImageUrl"
								:tooltip="t('fieldTooltip.volumetricImage')"
								:value="transaction.volumetricImageUrl"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(
										transaction.components,
										'inscriptionNumber',
										component => getUrlTrailingSegments(component.volumetricImageUrl, 2) || 'N/A',
										component => component.volumetricImageUrl
									)
								"
								:expand-all-values="expandAllSections.dataProvider"
								:collapsible-registry="collapsibleFieldIds.dataProvider"
							/>
						</div>
					</AccordionItem>

					<AccordionItem value="restrictions" :title="t('transaction.card.accordion.restrictions')">
						<div class="accordion-fields">
							<button
								v-if="collapsibleFieldIds.restrictions.size > 1"
								type="button"
								class="expand-all-toggle"
								@click="expandAllSections.restrictions = !expandAllSections.restrictions"
							>
								{{
									expandAllSections.restrictions
										? t("transaction.card.collapseAllValues")
										: t("transaction.card.expandAllValues")
								}}
							</button>

							<EditableField
								:readonly="!isEditMode"
								type="checkbox"
								:title="t('transaction.card.fields.tod')"
								name="tod"
								:tooltip="t('fieldTooltip.tod')"
								:value="transaction.tod"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'tod')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								multiple
								:title="t('transaction.card.fields.cptaq')"
								name="cptaq"
								:options="TransactionCPTAQ"
								:value="transaction.cptaq"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'cptaq')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								multiple
								:title="t('transaction.card.fields.urbanPerimeter')"
								name="urbanPerimeter"
								:options="TransactionUrbanPerimeter"
								:value="transaction.urbanPerimeter"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'urbanPerimeter')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								multiple
								:title="t('transaction.card.fields.infrastructure')"
								name="infrastructure"
								:options="TransactionInfrastructure"
								:value="transaction.infrastructure"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'infrastructure')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.landShape')"
								name="landShape"
								:options="TransactionLandShape"
								:value="transaction.landShape"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'landShape')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="text"
								:title="t('transaction.card.fields.parcelReport')"
								name="parcelReport"
								:value="transaction.parcelReport"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'parcelReport')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.contamination')"
								name="contamination"
								:options="TransactionContamination"
								:value="transaction.contamination"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'contamination')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.01"
								:title="t('transaction.card.fields.contaminationCost')"
								symbol="$"
								name="contaminationCost"
								:value="transaction.contaminationCost"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.contaminationCost)
								"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="checkbox"
								:title="t('transaction.card.fields.wetlands')"
								name="wetlands"
								:value="transaction.wetlands"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'wetlands')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="checkbox"
								:title="t('transaction.card.fields.floodableZone')"
								name="floodableZone"
								:value="transaction.floodableZone"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'floodableZone')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="checkbox"
								:title="t('transaction.card.fields.endangeredSpecies')"
								name="endangeredSpecies"
								:value="transaction.endangeredSpecies"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'endangeredSpecies')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.1"
								:title="t('transaction.card.fields.deduction')"
								symbol="%"
								name="deductionRatio"
								:value="transaction.deductionRatio"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component => component.deductionRatio)
								"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="textarea"
								:title="t('transaction.card.fields.deductionComments')"
								name="deductionComments"
								:value="transaction.deductionComments"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'deductionComments')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="select"
								:title="t('transaction.card.fields.deductionFiles')"
								name="deductionFiles"
								:value="transaction.deductionFiles"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="toMultiValues(transaction.components, 'inscriptionNumber', 'deductionFiles')"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.developableLandArea')"
								:symbol="areaUnitSymbol"
								:name="TransactionAreaField.DevelopableLandArea"
								:value="transactionAreaValue(transaction, TransactionAreaField.DevelopableLandArea, areaUnit)"
								@input="onFieldInput"
								disabled
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionAreaValue(component, TransactionAreaField.DevelopableLandArea, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="0.1"
								:title="t('transaction.card.fields.infrastructureDeduction')"
								symbol="%"
								name="infrastructureDeductionRatio"
								:value="transaction.infrastructureDeductionRatio"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(
										transaction.components,
										'inscriptionNumber',
										component => component.infrastructureDeductionRatio
									)
								"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="textarea"
								:title="t('transaction.card.fields.infrastructureDeductionComments')"
								name="infrastructureDeductionComments"
								:value="transaction.infrastructureDeductionComments"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', 'infrastructureDeductionComments')
								"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
							<EditableField
								:readonly="!isEditMode"
								type="number"
								:rounding="1"
								:title="t('transaction.card.fields.netLandArea')"
								:symbol="areaUnitSymbol"
								:name="TransactionAreaField.NetLandArea"
								:value="transactionAreaValue(transaction, TransactionAreaField.NetLandArea, areaUnit)"
								@input="onFieldInput"
								:disabled="!isEditMode"
								aggregation-type="choice"
								:multi-values="
									toMultiValues(transaction.components, 'inscriptionNumber', component =>
										transactionAreaValue(component, TransactionAreaField.NetLandArea, areaUnit)
									)
								"
								:expand-all-values="expandAllSections.restrictions"
								:collapsible-registry="collapsibleFieldIds.restrictions"
							/>
						</div>
					</AccordionItem>
				</AccordionRoot>
			</div>

			<div v-if="isLoading" class="loading-overlay">
				<div class="loading-header-actions">
					<button v-if="!compact" type="button" class="icon-button collapse-btn" @click="emit('collapse')">
						<AppIcon :name="IconName.PanelRightCollapse" :size="18" :stroke-width="1.2" />
					</button>
					<button type="button" class="icon-button collapse-btn" @click="emit('close')">
						<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
					</button>
				</div>
				<div class="skeleton-block skeleton-price"></div>
				<div class="skeleton-block skeleton-address"></div>
				<div class="skeleton-block skeleton-borough"></div>
				<div class="skeleton-block skeleton-badge"></div>
				<div class="skeleton-separator"></div>
				<div class="skeleton-block skeleton-row" v-for="n in 10" :key="n"></div>
			</div>
		</div>

		<SearchModal
			id="contact-modal"
			v-model:open="contactModalIsOpen"
			:results="contactResults"
			:placeholder="t('transaction.card.searchContactPlaceholder')"
			:create-url="CRM_CONTACT_URL"
			entity-label="contact"
			id-key="id"
			@add="handleAddContacts"
			@pattern="handleContactSearchInput"
		>
			<template #details="{ item: contact }">
				<span class="name">{{ contact.firstName }} {{ contact.lastName }}</span>
				<div class="primary-company">
					<span>{{ contact.primaryCompany?.name }}</span>
				</div>
				<span v-if="contact.address" class="address">{{ contact.address }}</span>
				<span v-else class="unknown-address">{{ $t("unknownAddress") }}</span>
				<span class="email">{{ contact.email }}</span>
			</template>
		</SearchModal>

		<SearchModal
			id="company-modal"
			ref="companyModal"
			v-model:open="companyModalIsOpen"
			:results="companyResults"
			:placeholder="t('transaction.card.searchCompanyPlaceholder')"
			:create-url="CRM_COMPANY_URL"
			entity-label="company"
			id-key="id"
			@add="companies => handleAddCompany(companies[0], companyModal!.targetContact)"
			@pattern="pattern => handleCompanySearchInput(pattern, companyModal!.targetContact)"
		>
			<template #details="{ item: company }">
				<span class="name">{{ company.name }}</span>
				<span class="domain">{{ company.domain }}</span>
				<span v-if="company.address" class="address">{{ company.address }}</span>
			</template>
		</SearchModal>
	</div>
</template>

<script setup lang="ts">
import { toRaw, ref, reactive, watch, computed, onMounted, inject, type Ref } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useElementSize } from "@vueuse/core"
import sortBy from "lodash.sortby"
import debounce from "lodash.debounce"
import { UseRootStore, Permission } from "@/model/RootStore"
import TransactionSelectionButton from "@/components/comparable-analyses/TransactionSelectionButton.vue"
import {
	type Contact,
	type Company,
	type CompanyList,
	type Transaction,
	TransactionSubcategory,
	TransactionSaleContext,
	TransactionPartyType,
	TransactionCurrentState,
	TransactionCodificationType,
	TransactionProjectApproval,
	TransactionProjectApprovalAtDeedOfSale,
	TransactionCPTAQ,
	TransactionUrbanPerimeter,
	TransactionInfrastructure,
	TransactionLandShape,
	TransactionContamination,
	TransactionMetadataStatus,
	TransactionMetadataBuyerStatus,
	TransactionFieldStatus,
} from "@/model/DataModel"
import {
	searchContacts,
	searchCompanies,
	addContactToTransaction,
	removeContactFromTransaction,
} from "@/utils/ApiClient"
import {
	formatInscriptionNumber,
	formatQuantity,
	roundTo,
	spaceOutNumbers,
	splitTransactionAddress,
} from "@/utils/format"
import { formatDateToISO, parseISODate } from "@/utils/date"
import { useUnitSystem } from "@/composables/useUnitSystem"
import {
	TransactionAreaField,
	transactionAreaInputValue,
	transactionAreaValue,
	TransactionPricePerAreaField,
	transactionPricePerAreaValue,
} from "@/utils/transactionUnitFields"
import { formatConstructionYears, formatPropertyUses } from "@/utils/transactionBuilding"

import { useI18n } from "vue-i18n"
import { AssetType, LandCategory, LandSubcategory } from "@/graphql"

import EditableField from "./EditableField.vue"
import TransactionParkingField from "./TransactionParkingField.vue"
import { createParkingUpdateQueue } from "@/utils/transactionParking"
import CopyButton from "@/components/ui/CopyButton.vue"
import SearchModal from "@/components/modals/SearchModal.vue"

import Button from "@/components/ui/Button.vue"

import ArkListboxDropdown from "../ui/ArkListboxDropdown.vue"
import AccordionRoot from "../ui/AccordionRoot.vue"
import AccordionItem from "../ui/AccordionItem.vue"
import FileUpload from "@/components/ui/ArkFileUpload.vue"
import Menu from "../ui/Menu.vue"
import MenuItem from "../ui/MenuItem.vue"

const Role = Object.freeze({
	Buyer: "buyer",
})

const transaction = defineModel<Transaction>("transaction", { required: true })

const props = defineProps<{
	item?: Transaction
	isLoading?: boolean
	compact?: boolean
}>()

const emit = defineEmits(["edit", "list-add", "close", "collapse", "header-resize", "update:editMode"])

// reports the header's height (incl. top padding/separator) so the mobile drawer can peek to exactly it
const headerEl = ref<HTMLElement | null>(null)
const itemsEl = ref<HTMLElement | null>(null)
const { height: headerHeight } = useElementSize(headerEl)
watch(
	headerHeight,
	() => {
		const peekHeight = itemsEl.value?.offsetTop ?? headerHeight.value
		if (peekHeight > 0) emit("header-resize", peekHeight)
	},
	{ immediate: true }
)

const expandAllSections = reactive({
	info: false,
	classification: false,
	pricing: false,
	location: false,
	parties: false,
	lender: false,
	zoning: false,
	development: false,
	dataProvider: false,
	restrictions: false,
})

const collapsibleFieldIds: Record<string, Set<number>> = reactive({
	info: new Set(),
	classification: new Set(),
	pricing: new Set(),
	location: new Set(),
	parties: new Set(),
	lender: new Set(),
	zoning: new Set(),
	development: new Set(),
	dataProvider: new Set(),
	restrictions: new Set(),
})

const companyModal = ref<{ targetContact: Contact | null } | null>(null)
const contactModalIsOpen = ref(false)
const companyModalIsOpen = ref(false)
const contactResults = ref<Contact[]>([])
const companyResults = ref<CompanyList>([])
const selectedContacts = ref([])
const municipalities = ref<string[]>([])

onMounted(async () => {
	municipalities.value = await store.getMunicipalities
})

function sortContacts(contacts: Contact[] | null | undefined) {
	return sortBy(contacts ?? [], ["firstName", "lastName", "id"])
}

const handleContactSearchInput = debounce(async (name: string) => {
	if (name.length < 1) {
		contactResults.value = []
		return
	}
	const currentContactIds = (toRaw(transaction.value.contacts) ?? []).flatMap(contact =>
		contact.id ? [contact.id] : []
	)

	const contacts = await searchContacts(store.userApiToken, name).then(all => {
		return all?.filter(c => c.id == null || !currentContactIds.includes(c.id)) || []
	})
	contactResults.value = contacts
}, 300)

async function handleCompanySearchInput(name: string, contact: Contact | null): Promise<void> {
	if (name.length < 1) {
		companyResults.value = []
		return
	}
	if (contact == null) {
		return
	}
	const currentCompany = contact.primaryCompany

	const companies = await searchCompanies(store.userApiToken, name).then(all => {
		return currentCompany ? all.filter(c => c.id !== currentCompany.id) : all
	})
	companyResults.value = companies || []
}

watch(contactModalIsOpen, isOpen => {
	if (!isOpen) {
		contactResults.value = []
		selectedContacts.value = []
		return
	}
})
watch(companyModalIsOpen, isOpen => {
	if (!isOpen) {
		companyResults.value = []
		if (companyModal.value != null) {
			companyModal.value.targetContact = null
		}
		return
	}
})

async function handleAddContacts(contacts: Contact[]) {
	if (!isWriter.value) {
		return
	}

	const errors: Contact[] = []
	const { inscriptionNumber } = transaction.value
	if (inscriptionNumber == null) {
		return
	}

	contacts.forEach(async contact => {
		if (contact.id == null) {
			errors.push(contact)
			return
		}

		const addedContact = await addContactToTransaction(
			store.userApiToken,
			contact.id,
			Role.Buyer,
			inscriptionNumber,
			contact.primaryCompany?.id || undefined
		)
		if (addedContact) {
			transaction.value.contacts = [...(transaction.value.contacts ?? []), addedContact]
		} else {
			errors.push(contact)
		}
	})
	if (errors.length > 0) {
		console.error(`Error adding contacts ${errors.join(", ")}`)
		// TODO display error
	}
	contactModalIsOpen.value = false
}
async function handleAddCompany(company: Company | null, contact: Contact | null): Promise<void> {
	if (!isWriter.value) {
		return
	}
	if (company?.id == null || contact?.id == null) {
		return
	}

	const { inscriptionNumber } = transaction.value
	if (inscriptionNumber == null) {
		return
	}

	const updatedContact = await addContactToTransaction(
		store.userApiToken,
		contact.id,
		Role.Buyer,
		inscriptionNumber,
		company.id
	)
	if (updatedContact) {
		contact.primaryCompany = updatedContact.primaryCompany
	} else {
		console.error(`Error adding company ${updatedContact}`)
	}
	companyModalIsOpen.value = false
}

async function handleRemoveContact(transaction: Transaction, contact: Contact): Promise<void> {
	const lines = []
	lines.push(t("transaction.card.confirmRemoveContact"))
	lines.push("")
	lines.push(`${contact.firstName} ${contact.lastName} (${contact.email})`)
	lines.push("")
	if (contact.address) {
		lines.push(contact.address)
		lines.push("")
	}
	lines.push(t("transaction.card.fromTransaction", { inscriptionNumber: transaction.inscriptionNumber }))

	const text = lines.join("\r\n")
	const ok = window.confirm(text)
	if (ok) {
		const contactId = contact.id
		const inscriptionNumber = transaction.inscriptionNumber

		if (inscriptionNumber == null || contactId == null) {
			console.error(`Error removing contact ${contactId}`)
			return
		}

		const ok = await removeContactFromTransaction(store.userApiToken, inscriptionNumber, contactId)
		if (ok) {
			const contacts = transaction.contacts?.filter(({ id }) => id != contact.id) || []
			transaction.contacts = contacts
			return
		}
		console.error(`Error removing contact ${contactId}`)
		return
	}
}

const store = UseRootStore()

const isLargeScreen = inject<Ref<boolean>>("isLargeScreen")

// the card stays read-only on mobile regardless of TransactionWrite permission
const canWrite = !!store.currentUser?.has(Permission.TransactionWrite)
const isWriter = computed(() => canWrite && (isLargeScreen?.value ?? true))
const canUploadDocument = store.currentUser?.has(Permission.DocumentStoreUpload)

// writers land in edit mode by default; everyone else stays read-only
const isEditModeRequested = ref(canWrite)
const isEditMode = computed(() => isEditModeRequested.value && (isLargeScreen?.value ?? true))
watch(isEditMode, val => emit("update:editMode", val), { immediate: true })
function toggleEditMode(): void {
	isEditModeRequested.value = !isEditModeRequested.value
}
function onEditMenuSelect(details: { value: string }): void {
	if (details.value === "info") toggleEditMode()
	else if (details.value === "composition") emit("edit")
}
const canReadConfidential = store.currentUser?.has(Permission.TransactionSensitiveRead)

const addressLines = computed(() =>
	splitTransactionAddress(
		transaction.value.location?.address,
		transaction.value.location?.borough,
		transaction.value.location?.municipality
	)
)

const isParcelsExpanded = ref(false)
const dedupedParcelNumbers = computed<string[]>(() =>
	spaceOutNumbers(...Array.from(new Set(transaction.value.location?.parcelNumbers ?? [])))
)

async function handleFileUpload(file: File) {
	const { inscriptionNumber } = transaction.value
	if (!inscriptionNumber) return
	const buffer = await file.arrayBuffer()
	const bytes = new Uint8Array(buffer)
	let binary = ""
	for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
	const fileBase64 = btoa(binary)
	const result = await store.updateTransaction(inscriptionNumber, {
		fileBase64,
		fileName: file.name,
		mimeType: file.type,
	} as Partial<Transaction>)
	transaction.value = result
}
const isAssemblyTransaction = computed(() => (transaction.value.components?.length ?? 0) > 0)
const canEditAssembly = computed(() => isWriter.value && isAssemblyTransaction.value)

function getUrlTrailingSegments(url: string | null | undefined, count: number = 1) {
	if (!url) {
		return null
	}

	const u = new URL(url)

	// Split the pathname into segments
	const segments = u.pathname.split("/").filter(Boolean)

	// Extract the last `count` segments
	return segments.slice(-count).join("/")
}

function normalizeParcelIds(idList: string) {
	return idList.replace(/ /g, "").split(",").filter(Boolean)
}

type MultiValueSelector<T, R> = keyof T | ((item: T) => R)

function getMultiValue<T, R>(item: T, selector: MultiValueSelector<T, R>) {
	return typeof selector === "function" ? selector(item) : (item[selector] as R)
}

function hasDisplayValue(value: unknown): boolean {
	if (value == null) return false
	if (typeof value === "string") return value.trim().length > 0
	if (Array.isArray(value)) return value.length > 0
	return true
}

function toMultiValues<T, I extends keyof T, V>(
	items: T[] | null | undefined,
	idKey: I,
	valueSelector: MultiValueSelector<T, V>,
	hrefSelector?: MultiValueSelector<T, string | null | undefined>
): { id: string; value: V; href?: string | null }[] | undefined {
	return items?.flatMap(item => {
		const id = item[idKey]
		const value = getMultiValue(item, valueSelector)
		const href = hrefSelector ? getMultiValue(item, hrefSelector) : undefined

		return id == null || !hasDisplayValue(value) ? [] : [{ id: String(id), value, href }]
	})
}

function formatTransactionDate(value: Date | string | null | undefined): string | null {
	if (!value) return null
	if (value instanceof Date) return formatDateToISO(value)
	return value
}

function toLocalDateValue(value: unknown): Date | null | unknown {
	if (typeof value !== "string") return value
	return /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseISODate(value) : value
}

function applyLocalFieldInput(name: string, value: any) {
	switch (name) {
		case "address":
			transaction.value.location = {
				...transaction.value.location,
				address: value,
			}
			return
		case "municipalityName":
			transaction.value.location = {
				...transaction.value.location,
				municipality: value,
			}
			return
		case "buyer":
			transaction.value.buyer = {
				...transaction.value.buyer,
				name: value,
			}
			return
		case "buyerType":
			transaction.value.buyer = {
				...transaction.value.buyer,
				type: value,
			}
			return
		case "buyerNeq":
			transaction.value.buyer = {
				...transaction.value.buyer,
				neq: value,
			}
			return
		case "buyerActivity":
			transaction.value.buyer = {
				...transaction.value.buyer,
				activity: value,
			}
			return
		case "metadataBuyerStatus":
			transaction.value.metadata = {
				...transaction.value.metadata,
				buyerStatus: value,
			}
			return
		case "metadataStoriesStatus":
			transaction.value.metadata = {
				...transaction.value.metadata,
				storiesStatus: value,
			}
			return
		case "metadataBuildableAreaStatus":
			transaction.value.metadata = {
				...transaction.value.metadata,
				buildableAreaStatus: value,
			}
			return
		case "metadataNumberOfUnitsStatus":
			transaction.value.metadata = {
				...transaction.value.metadata,
				numberOfUnitsStatus: value,
			}
			return
		case "seller":
			transaction.value.seller = {
				...transaction.value.seller,
				name: value,
			}
			return
		case "sellerType":
			transaction.value.seller = {
				...transaction.value.seller,
				type: value,
			}
			return
		case "sellerNeq":
			transaction.value.seller = {
				...transaction.value.seller,
				neq: value,
			}
			return
		case "sellerActivity":
			transaction.value.seller = {
				...transaction.value.seller,
				activity: value,
			}
			return
		case "psaDate":
		case "deedOfSaleDate":
			;(transaction.value as any)[name] = toLocalDateValue(value)
			return
		default:
			;(transaction.value as any)[name] = value
	}
}

let parkingUpdateRevision = 0
const queueParkingUpdate = createParkingUpdateQueue()

async function onFieldInput({ name, value }: { name: string; value: any }) {
	if (!isWriter.value) {
		return
	}

	const { inscriptionNumber } = transaction.value
	const sourceValue = transactionAreaInputValue(name, value, areaUnit.value)
	const isParkingUpdate = ["parkingTypes", "parkingIndoorCount", "parkingOutdoorCount"].includes(name)
	const revision = isParkingUpdate ? ++parkingUpdateRevision : 0

	applyLocalFieldInput(name, sourceValue)
	const save = () => store.updateTransaction(inscriptionNumber, { [name]: sourceValue })
	const updated = await (isParkingUpdate ? queueParkingUpdate(save) : save())
	if (
		isParkingUpdate &&
		revision === parkingUpdateRevision &&
		transaction.value.inscriptionNumber === inscriptionNumber
	) {
		transaction.value.parkingTypes = updated.parkingTypes
		transaction.value.hasIndoorParking = updated.hasIndoorParking
		transaction.value.hasOutdoorParking = updated.hasOutdoorParking
	}

	if (name === "stories") transaction.value.metadata!.storiesStatus = updated.metadata?.storiesStatus
	if (name === "buildableAreaSqft")
		transaction.value.metadata!.buildableAreaStatus = updated.metadata?.buildableAreaStatus
	if (name === "numberOfUnits") transaction.value.metadata!.numberOfUnitsStatus = updated.metadata?.numberOfUnitsStatus
}

async function onClassificationUpdate({ name, value }: { name: string; value: any }) {
	if (!isWriter.value) {
		return
	}

	const { inscriptionNumber } = transaction.value

	// if we add land_category_new asset_type have to contain 'land'
	if (name === "landCategoryNew" && value?.length > 0) {
		const currentAssetType = transaction.value.assetType || []
		const hasLand = currentAssetType.some(v => v === AssetType.Land)
		const assetTypeToSend = hasLand ? currentAssetType : [...currentAssetType, AssetType.Land]

		// Update local state
		transaction.value.landCategoryNew = value
		if (!hasLand) {
			transaction.value.assetType = assetTypeToSend
		}

		await store.updateTransaction(inscriptionNumber, {
			assetType: assetTypeToSend,
			landCategoryNew: value,
		})
		return
	}

	// if we add land_subcategory, ensure we send ALL required fields
	if (name === "landSubcategory" && value?.length > 0) {
		const currentLandCategory = transaction.value.landCategoryNew || []
		const currentAssetType = transaction.value.assetType || []

		const hasLand = currentAssetType.some(v => v === AssetType.Land)
		const assetTypeToSend = hasLand ? currentAssetType : [...currentAssetType, AssetType.Land]

		transaction.value.landSubcategory = value
		if (!hasLand) {
			transaction.value.assetType = assetTypeToSend
		}

		await store.updateTransaction(inscriptionNumber, {
			assetType: assetTypeToSend,
			landCategoryNew: currentLandCategory,
			landSubcategory: value,
		})
		return
	}

	// if asset_type does not include 'land' clear land_category_new and land_subcategory
	if (name === "assetType") {
		const hasLand = value?.some((v: string | null | undefined) => v === AssetType.Land)
		if (!hasLand && (transaction.value.landCategoryNew?.length || transaction.value.landSubcategory?.length)) {
			transaction.value.landCategoryNew = []
			transaction.value.landSubcategory = []
			await store.updateTransaction(inscriptionNumber, {
				assetType: value,
				landCategoryNew: [],
				landSubcategory: [],
			})
			return
		}
	}

	;(transaction.value as any)[name] = value
	await store.updateTransaction(inscriptionNumber, { [name]: value })
}

const selectedStatus = ref<string | null>(null)

const codificationDate = computed(() => transaction.value.metadata?.doneAt ?? null)

const buyerCodificationDate = computed(() => transaction.value.metadata?.buyerDoneAt ?? null)

watch(
	() => transaction.value,
	newVal => {
		if (newVal) {
			selectedStatus.value = newVal.metadata?.status || null
		}
	},
	{ immediate: true }
)

function onStatusChange(value: string | number | (string | number | null)[] | null) {
	if (typeof value !== "string" || value === null) {
		return
	}
	const status = value
	const { inscriptionNumber } = transaction.value
	selectedStatus.value = status
	store.updateTransaction(inscriptionNumber, { metadataStatus: status })
}

function capitalizeWords(value: string): string {
	return value.replace(/\b\w/g, char => char.toUpperCase())
}

const statusOptions = computed(() =>
	Object.values(TransactionMetadataStatus).map(status => ({
		value: status,
		label: capitalizeWords(status),
	}))
)

const buyerStatusOptions = computed(() => Object.values(TransactionMetadataBuyerStatus))

const fieldStatusOptions = computed(() =>
	Object.values(TransactionFieldStatus).map(value => ({
		value,
		label: t(`transaction.card.fieldStatus.${value}`),
	}))
)

const CRM_CONTACT_URL = import.meta.env.VITE_HUBSPOT_CREATE_CONTACT_URL
const CRM_COMPANY_URL = import.meta.env.VITE_HUBSPOT_CREATE_COMPANY_URL

const { t } = useI18n()
const { areaUnit, areaUnitSymbol, pricePerAreaUnitSymbol } = useUnitSystem()

const hasLandAssetType = computed(() => {
	const assetType = transaction.value.assetType || []
	return assetType.some(type => type === AssetType.Land)
})

const hasRelevantLandCategory = computed(() => {
	if (!transaction.value.landCategoryNew?.length) return false

	const relevantCategories = [
		LandCategory.Residential,
		LandCategory.Commercial,
		LandCategory.Industrial,
		LandCategory.Agricultural,
	]
	return transaction.value.landCategoryNew.some(cat => cat != null && relevantCategories.includes(cat as LandCategory))
})

const assetTypeOptions = computed(() => {
	return Object.values(AssetType).map(value => ({
		value,
		label: t(`property.assetType.${value}`),
	}))
})

const landCategoryOptions = computed(() => {
	return Object.values(LandCategory).map(value => ({
		value,
		label: t(`property.landCategory.${value}`),
	}))
})

const landSubcategoryOptions = computed(() => {
	const selectedCategories = transaction.value.landCategoryNew || []
	const allOptions: { value: string; label: string }[] = []

	// residential
	if (selectedCategories.includes(LandCategory.Residential)) {
		allOptions.push(
			{ value: LandSubcategory.LowRise, label: t("property.landSubcategory.LowRise") },
			{ value: LandSubcategory.MidRise, label: t("property.landSubcategory.MidRise") },
			{ value: LandSubcategory.HighRise, label: t("property.landSubcategory.HighRise") },
			{ value: LandSubcategory.TownHouse, label: t("property.landSubcategory.TownHouse") },
			{ value: LandSubcategory.SingleFamily, label: t("property.landSubcategory.SingleFamily") }
		)
	}

	// commercial
	if (selectedCategories.includes(LandCategory.Commercial)) {
		allOptions.push(
			{ value: LandSubcategory.LowRise, label: t("property.landSubcategory.LowRise") },
			{ value: LandSubcategory.MidRise, label: t("property.landSubcategory.MidRise") },
			{ value: LandSubcategory.HighRise, label: t("property.landSubcategory.HighRise") }
		)
	}

	// industrial
	if (selectedCategories.includes(LandCategory.Industrial)) {
		allOptions.push(
			{ value: LandSubcategory.LightMedium, label: t("property.landSubcategory.LightMedium") },
			{ value: LandSubcategory.Heavy, label: t("property.landSubcategory.Heavy") }
		)
	}

	// agricultural
	if (selectedCategories.includes(LandCategory.Agricultural)) {
		allOptions.push(
			{ value: LandSubcategory.FullyCultivated, label: t("property.landSubcategory.FullyCultivated") },
			{ value: LandSubcategory.MostlyCultivated, label: t("property.landSubcategory.MostlyCultivated") },
			{ value: LandSubcategory.HalfAndHalf, label: t("property.landSubcategory.HalfAndHalf") },
			{ value: LandSubcategory.MostlyForestry, label: t("property.landSubcategory.MostlyForestry") },
			{ value: LandSubcategory.FullForestry, label: t("property.landSubcategory.FullForestry") }
		)
	}

	const seen = new Set<string>()
	return allOptions.filter(option => {
		if (seen.has(option.value)) return false
		seen.add(option.value)
		return true
	})
})
</script>

<style scoped>
@keyframes blink {
	50% {
		opacity: 0.2;
	}
}

.loading-overlay {
	position: absolute;
	inset: 0;
	z-index: 1;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
	padding: var(--spacing-card-padding-sm);
	background-color: var(--neutral-0);
	overflow: hidden;

	.loading-header-actions {
		align-self: flex-end;
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);

		.icon-button {
			display: flex;
			align-items: center;
			justify-content: center;
			width: var(--size-icon-xl);
			height: var(--size-icon-xl);
			padding: 0;
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-full);
			background: var(--neutral-0);
			color: var(--neutral-600);
			cursor: pointer;

			&.collapse-btn {
				border-color: transparent;
			}
		}
	}

	.skeleton-block {
		border-radius: var(--border-radius-base);
		background-color: var(--neutral-200);
		animation: blink 1.4s ease-in-out infinite;
	}

	.skeleton-price {
		width: 140px;
		height: 28px;
	}

	.skeleton-address {
		width: 70%;
		height: 18px;
	}

	.skeleton-borough {
		width: 45%;
		height: 14px;
	}

	.skeleton-badge {
		width: 90px;
		height: 20px;
		border-radius: var(--border-radius-full);
	}

	.skeleton-separator {
		height: var(--border-width-thin);
		background-color: var(--neutral-200);
	}

	.skeleton-row {
		width: 100%;
		height: 32px;
		flex-shrink: 0;
	}
}

.card-detailed {
	container-type: inline-size;
	container-name: transaction-card-detailed;
	display: flex;
	flex-direction: column;
	height: 100%;
	background-color: var(--neutral-0);
	overflow: hidden;
}

.transaction {
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	gap: var(--spacing-md);
	padding: var(--spacing-card-padding-sm);
	overflow: hidden;
	background-color: var(--neutral-0);

	& > .header {
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		.header-top {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
			gap: var(--spacing-sm);
			margin-top: var(--spacing-xs);
		}

		.header-actions {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			flex-shrink: 0;
		}

		.icon-button {
			display: flex;
			align-items: center;
			justify-content: center;
			width: var(--size-icon-xl);
			height: var(--size-icon-xl);
			padding: 0;
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-full);
			background: var(--neutral-0);
			color: var(--neutral-600);
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				color: var(--primary-green);
				border-color: var(--primary-green);
			}

			&.collapse-btn {
				width: auto;
				height: auto;
				padding: var(--spacing-sm);
				border: none;
				border-radius: var(--border-radius-sm);
				background: none;

				&:hover {
					background: var(--neutral-100);
					color: var(--neutral-900);
				}
			}
		}

		.price-block {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xxs);
		}

		.price-label {
			color: var(--neutral-600);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
		}

		.price {
			font-family: var(--font-family-primary);
			font-size: var(--font-size-2xl);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);

			.currency {
				color: var(--primary-green);
				padding-right: var(--spacing-sm);
			}

			.amount {
				color: var(--primary-green);
			}
		}

		.address {
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-medium);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
			max-width: var(--width-transaction-field);
			overflow-wrap: break-word;

			&::first-letter {
				text-transform: uppercase;
			}

			&.unavailable {
				color: var(--neutral-200);
			}
		}

		.badge-actions-row {
			display: flex;
			justify-content: space-between;
			align-items: flex-end;
			flex-wrap: wrap;
			gap: var(--spacing-sm);
		}

		.badge-row {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			gap: var(--spacing-xs);
		}

		.type-badge {
			display: inline-flex;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background-color: var(--accent-transaction);
			color: var(--neutral-0);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
		}

		.status-badge {
			display: inline-flex;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background-color: rgb(200 125 95 / 12%);
			color: var(--accent-transaction);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
			text-transform: capitalize;
		}

		.price-row-actions {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
		}

		@container transaction-card-detailed (max-width: 360px) {
			.price-row-actions {
				flex-direction: column;
				align-items: flex-end;
				margin-left: auto;
			}

			.badge-row {
				flex-direction: column;
				align-items: flex-start;
			}
		}
	}

	.borough {
		color: var(--neutral-600);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-regular);
		line-height: var(--line-height-normal);
	}

	.separator {
		width: 100%;
		height: var(--border-width-thin);
		flex-shrink: 0;
		background-color: var(--neutral-400);
	}

	.items {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
		overflow-y: auto;

		:deep(.accordion-content) {
			padding-bottom: var(--spacing-md);
			padding-left: var(--spacing-sm);
			padding-right: var(--spacing-sm);
		}

		.accordion-item {
			.accordion-trigger {
				width: 100%;
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: var(--spacing-md) var(--spacing-sm);
				background: var(--neutral-0);
				border: none;
				cursor: pointer;
				text-align: left;
				transition: background-color 0.2s ease;
				min-height: 44px;
				font-size: var(--font-size-regular);

				&:hover,
				&:focus {
					background-color: var(--neutral-0);
					outline: none;
				}

				.accordion-legend {
					font-family: var(--font-family-primary);
					font-size: var(--font-size-regular);
					font-weight: var(--font-weight-medium);
					text-transform: capitalize;
					color: var(--neutral-900);
					margin: 0;
					padding: 0;
				}

				.accordion-icon {
					display: flex;
					align-items: center;
					justify-content: center;
					width: var(--size-icon-xl);
					height: var(--size-icon-xl);
					color: var(--neutral-600);
					transition: all 0.2s ease;
					flex-shrink: 0;

					.icon-text {
						font-size: var(--font-size-base);
						font-weight: normal;
						line-height: 1;
						user-select: none;
					}
				}

				&:hover .accordion-icon {
					color: var(--primary-green);
				}
			}

			.accordion-content-wrapper {
				overflow: visible;
				transition: all 0.2s ease-out;
				background-color: var(--neutral-0);

				.accordion-content {
					padding: var(--spacing-lg) var(--spacing-sm);
					background-color: var(--neutral-0);
				}
			}

			&[data-state="open"] .accordion-trigger {
				background-color: var(--neutral-0);
			}
		}

		.transaction-status {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-md);
			padding: 0 var(--spacing-xs);

			.status {
				display: flex;
				flex-direction: row;
				gap: var(--spacing-xs);

				.label {
					flex: 0 0 var(--flex-basis-field);
					align-self: center;
					font-family: var(--font-family-primary);
					font-size: var(--font-size-small);
					font-weight: var(--font-weight-medium);
					color: var(--neutral-600);
					margin: 0;
				}

				.control {
					display: flex;
					flex: 1;
					flex-direction: column;
					gap: var(--spacing-xs);

					.status-dropdown {
						width: 100%;
					}
				}
			}
		}

		@keyframes pulse {
			0%,
			100% {
				opacity: 0.9;
				transform: scale(1);
			}
			50% {
				opacity: 1;
				transform: scale(1.02);
			}
		}

		.details {
			display: flex;
			flex-direction: column;
			gap: var(--grid-column-md);

			fieldset {
				min-width: 0;
				display: flex;
				flex-direction: column;
				justify-content: space-between;
				gap: var(--spacing-md);
				border: var(--border-width-thin) solid var(--neutral-200);
				border-radius: var(--border-radius-base);
				padding: var(--spacing-lg) var(--spacing-sm) var(--spacing-md) var(--spacing-sm);
				background-color: var(--neutral-0);

				legend {
					font-family: var(--font-family-primary);
					font-size: var(--font-size-2xl);
					font-weight: var(--font-weight-bold);
					text-transform: capitalize;
					color: var(--neutral-600);
					padding: 0 var(--spacing-xs);
				}

				h2 {
					display: flex;
					justify-content: center;
					align-items: center;
					gap: var(--spacing-xs);
					padding: var(--spacing-xs) 0;
					background-color: var(--neutral-50);
					color: var(--neutral-600);
					font-family: var(--font-family-primary);
					font-weight: var(--font-weight-bold);
					text-transform: capitalize;
					margin-top: var(--spacing-xs);
					border-radius: var(--border-radius-sm);

					&::before,
					&::after {
						content: "───";
						color: var(--neutral-400);
					}
				}
			}
		}
		.contacts {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xs);

			.contact {
				padding: var(--spacing-sm);
				border: var(--border-width-thin) solid var(--neutral-200);
				border-radius: var(--border-radius-md);
				background: var(--neutral-0);
				transition: background-color 0.2s ease;

				&:hover {
					background: var(--neutral-50);
				}

				.info {
					display: flex;
					flex-direction: column;
					gap: var(--spacing-2xs);
					font-family: var(--font-family-primary);
					font-size: var(--font-size-small);

					.header {
						display: flex;
						justify-content: space-between;
						align-items: flex-start;
						gap: var(--spacing-xs);
					}

					.name {
						font-weight: var(--font-weight-medium);
						color: var(--neutral-900);
						overflow-wrap: break-word;
						flex: 1;

						.company {
							font-weight: var(--font-weight-regular);
							color: var(--neutral-600);
						}
					}

					.email {
						color: var(--neutral-600);
						overflow: hidden;
						text-overflow: ellipsis;
						white-space: nowrap;
					}

					.change-company-link {
						align-self: flex-start;
						margin-top: var(--spacing-xxs);
					}

					.remove {
						flex-shrink: 0;
						width: 20px;
						height: 20px;
						display: flex;
						align-items: center;
						justify-content: center;
						padding: 0;
						border: none;
						background: none;
						color: var(--neutral-600);
						cursor: pointer;
						border-radius: var(--border-radius-sm);
						transition: all 0.2s ease;

						&:hover {
							color: var(--primary-green);
							background-color: var(--neutral-200);
						}
					}
				}
			}
		}
	}
}

:is(#contact-modal, #company-modal) {
	&:deep(.details) {
		display: flex;
		flex-direction: column;
		width: 100%;
		gap: var(--spacing-xxs);

		.name {
			font-family: var(--font-family-primary);
			font-weight: var(--font-weight-bold);
			color: var(--neutral-900);
		}

		.unknown-address {
			font-family: var(--font-family-primary);
			font-style: italic;
			color: var(--neutral-600);
			opacity: 0.5;
		}
	}
}

#company-modal {
	.domain:empty {
		display: none;
	}
}

.expand-all-toggle {
	align-self: flex-end;
	padding: 0;
	border: none;
	background: none;
	font-family: var(--font-family-primary);
	font-size: var(--font-size-xs);
	color: var(--neutral-600);
	cursor: pointer;

	&:hover {
		color: var(--primary-green);
	}
}

.accordion-fields {
	display: flex;
	flex-direction: column;
	gap: clamp(15.2px, 0.8333vw, 16px); /* 95% floor */

	h2 {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-bold);
		color: var(--neutral-800);
		text-transform: uppercase;
		letter-spacing: 0.5px;

		&::after {
			content: "";
			flex: 1;
			height: var(--border-width-thin);
			background-color: var(--neutral-200);
		}
	}

	.title {
		font-size: var(--font-size-base);
		color: var(--neutral-600) !important;
	}

	.parcels-wrapper {
		display: flex;
		flex-direction: column;

		.parcels-toggle {
			display: block;
			width: 100%;
			margin-bottom: var(--spacing-xxs);
			text-align: right;
			background: none;
			border: none;
			padding: 0;
			font: inherit;
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			color: var(--neutral-600);
			cursor: pointer;

			&:hover {
				color: var(--primary-green);
			}
		}
	}

	.detail.parcels-detail {
		display: flex;
		align-items: baseline;
		gap: var(--spacing-xs);
		padding: var(--spacing-xs) 0 var(--spacing-sm);
		border-bottom: var(--border-width-thin) solid var(--neutral-100);
		font-size: var(--font-size-small);

		.label {
			flex: 0 0 var(--flex-basis-field);
			color: var(--neutral-600);
		}

		.value {
			flex: 1;
			min-width: 0;
			color: var(--neutral-900);
			text-align: end;

			&.empty {
				color: var(--neutral-400);
			}
		}

		.parcels-count {
			font-size: var(--font-size-xxs);
			color: var(--neutral-600);
		}

		.copy {
			align-self: center;
		}
	}
}

.accordion-fields :deep(.editable-field.is-readonly:last-child),
.accordion-fields :deep(.editable-field.is-readonly:has(+ h2)) {
	border-bottom: none;
}

.drive-upload-section {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);

	.drive-upload-field {
		display: flex;
		justify-content: space-between;
		gap: var(--spacing-xs);
		align-items: center;

		.drive-label {
			flex: 0 0 var(--flex-basis-field);
			font-weight: var(--font-weight-medium);
			font-size: var(--font-size-small);
			color: var(--neutral-600);
		}

		.link-pill {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-xxs);
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background-color: var(--neutral-100);
			color: var(--neutral-700);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-medium);
			text-decoration: none;
			transition:
				background-color 0.2s ease,
				color 0.2s ease;

			&:hover {
				background-color: var(--neutral-200);
				color: var(--neutral-900);
			}
		}

		.drive-link-group {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-sm);
		}

		.drive-file-count {
			font-size: var(--font-size-xs);
			color: var(--neutral-500);
		}

		.drive-empty {
			font-size: var(--font-size-small);
			color: var(--neutral-400);
		}
	}
}

.price-row-actions :deep(.select-btn),
.price-row-actions :deep(.toggle-edit-btn) {
	min-width: 0;
	width: fit-content;
}

.menu-trigger-chevron {
	margin-left: var(--spacing-xxs);
	flex-shrink: 0;
}
</style>
